import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { sponsorshipKeys } from "./keys";
import { paymentKeys } from "../payments/keys";
import {
  createSponsorship,
  deleteSponsorship,
  getAllSponsorships,
  getSponsorshipsPage,
  fetchSponsorshipMeta,
  getSponsorship,
  updateSponsorship,
  recordSponsorshipPayment,
} from "./api";
import { toast } from "sonner";

const STALE = 5 * 60 * 1000; // 5 min — matches server-side chapter cache TTL
const GC = 10 * 60 * 1000; // keep alive 10 min after last subscriber

// Single sponsorship — fresh data for detail views
export const useSponsorship = (id) => {
  return useQuery({
    queryKey: sponsorshipKeys.detail(id),
    queryFn: () => getSponsorship(id),
    enabled: !!id,
    staleTime: STALE,
    gcTime: GC,
  });
};

// Flat fetch — used by overview and other pages that need the full array
export const useSponsorships = (options = {}) => {
  return useQuery({
    queryKey: sponsorshipKeys.list(),
    queryFn: getAllSponsorships,
    staleTime: STALE,
    gcTime: GC,
    placeholderData: keepPreviousData,
    ...options,
  });
};

// Paginated infinite fetch — used by admin donors/community tables
export const useInfiniteSponsorships = (filters = {}) => {
  return useInfiniteQuery({
    queryKey: sponsorshipKeys.list(filters),
    queryFn: ({ pageParam = 1 }) =>
      getSponsorshipsPage({ ...filters, page: pageParam, limit: 50 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage;
      return page < pages ? page + 1 : undefined;
    },
    staleTime: STALE,
    gcTime: GC,
    placeholderData: keepPreviousData,
  });
};

// Lightweight meta fetch — chapters with data + mahallu names for a chapter
// Pass null as params to disable (e.g. when no chapter is selected yet)
export const useSponsorshipMeta = (params) => {
  return useQuery({
    queryKey: ["sponsorships", "meta", params],
    queryFn: () => fetchSponsorshipMeta(params),
    enabled: params !== null,
    staleTime: STALE,
    gcTime: GC,
  });
};

// Mutations — invalidate sponsorshipKeys.lists() so ALL list variants
// (both useSponsorships and useInfiniteSponsorships with any filters) are refreshed
export const useCreateSponsorship = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSponsorship,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sponsorshipKeys.lists() });
      toast.success("Sponsorship created successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create sponsorship",
      );
    },
  });
};

export const useUpdateSponsorship = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSponsorship,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sponsorshipKeys.lists() });
      toast.success("Sponsorship updated successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update sponsorship",
      );
    },
  });
};

export const useDeleteSponsorship = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSponsorship,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sponsorshipKeys.lists() });
      toast.success("Sponsorship deleted successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete sponsorship",
      );
    },
  });
};

export const useRecordSponsorshipPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: recordSponsorshipPayment,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: sponsorshipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: sponsorshipKeys.details() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      if (result?.isFollowUpAttributed) {
        toast.success("Payment recorded via Follow-Up");
      } else {
        toast.success("Payment recorded successfully");
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to record payment");
    },
  });
};
